'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react'
import type { ProcessedTheme } from './DomainEntry/processedTypes'
import { watchClasses } from './DomainEntry/processedTypes'
import { ScreenshotLightbox, ImageTile } from './DomainEntry/imageComponents'
import { CommunicationMockup } from '@shared/postui'
import type { Communication } from '@shared/postui'
import { WidgetModal } from '../Widget'
import { useIsMobile } from '@/hooks/use-mobile'

export function OriginalSubmissionWidget({
  r2Key,
  theme,
  getToken,
  bare = false,
  onImageLoaded,
  communication,
  className = '',
  postType,
  messageTypeId,
  messageTypeText,
}: {
  r2Key: string | null | undefined
  theme: ProcessedTheme
  getToken: () => Promise<string>
  bare?: boolean
  onImageLoaded?: (base64: string, mimeType: string) => void
  communication?: Communication
  className?: string
  postType?: string | null
  messageTypeId?: number | string | null
  messageTypeText?: string | null
}) {
  const c = watchClasses(theme)
  const [src, setSrc] = useState<string | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')
  const [lightbox, setLightbox] = useState(false)
  const [expanded, setExpanded] = useState(false)

  // Same mobile-default-collapsed behavior as EntryDetailPanel (see that
  // file) — this widget isn't wrapped in one (it renders its own section/
  // header to support the bare variant below), so it needs its own copy.
  const isMobile = useIsMobile()
  const [collapsed, setCollapsed] = useState(false)
  const appliedMobileDefault = useRef(false)
  useEffect(() => {
    if (isMobile && !appliedMobileDefault.current) {
      appliedMobileDefault.current = true
      setCollapsed(true)
    }
  }, [isMobile])

  const load = useCallback(async () => {
    if (!r2Key) return
    setState('loading')
    try {
      const token = await getToken()
      const res = await fetch('/api/screenshots/bykey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key, turnstileToken: token }),
      })
      const result = await res.json()
      if (!result.success) { setState('error'); return }
      const { data: rawBase64, contentType } = result.data?.data?.buffer ?? {}
      if (!rawBase64) { setState('error'); return }
      const mime = contentType ?? 'image/jpeg'
      setSrc(`data:${mime};base64,${rawBase64}`)
      setState('loaded')
      onImageLoaded?.(rawBase64, mime)
    } catch {
      setState('error')
    }
  }, [r2Key, getToken])

  useEffect(() => {
    const t = setTimeout(load, 1200)
    return () => clearTimeout(t)
  }, [load])

  const tile = (
    <>
      {lightbox && src && <ScreenshotLightbox src={src} label="Original submission" onClose={() => setLightbox(false)} />}
      <ImageTile
        label="Original report"
        src={src}
        fallback={
          !r2Key ? <span className={c.faint}>No submission image</span>
          : state === 'loading' ? 'Loading image'
          : state === 'error' ? <button onClick={load} className="text-destructive">Retry</button>
          : <span className={c.faint}>No submission image</span>
        }
        onExpand={() => setLightbox(true)}
      />
    </>
  )

  // Whether to show the image tile at all depends only on whether an image
  // was ever submitted (r2Key) — independent of whether the communication
  // mockup shows, which depends only on whether `communication` was built.
  // Tying the mockup's visibility to the tile's load state (as a previous
  // version of this did) meant a still-loading or failed image fetch hid an
  // otherwise-ready mockup entirely.
  const showTile = !!r2Key
  const content = showTile && communication ? (
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">{tile}</div>
      <div className="shrink-0 max-w-[50%] overflow-hidden">
        <CommunicationMockup communication={communication} theme={theme} selectable />
      </div>
    </div>
  ) : showTile ? (
    tile
  ) : communication ? (
    <CommunicationMockup communication={communication} theme={theme} selectable />
  ) : (
    <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No submission preview available</p>
  )

  const canExpand = !!(src || communication)

  if (bare) return (
    <>
      {expanded && canExpand && (
        <WidgetModal label="Original submission" theme={theme} onClose={() => setExpanded(false)}>
          {content}
        </WidgetModal>
      )}
      <div className="mb-2 flex items-center justify-between">
        <span className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>// Original submission</span>
        {canExpand && (
          <button onClick={() => setExpanded(true)} className={`rounded p-1 transition-colors hover:text-signal ${c.faint}`} aria-label="Expand">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {content}
    </>
  )

  return (
    <section className={`flex flex-col border overflow-hidden ${c.panel} ${c.line} ${className}`}>
      {expanded && canExpand && (
        <WidgetModal label="Original submission" theme={theme} onClose={() => setExpanded(false)}>
          {content}
        </WidgetModal>
      )}
      <div
        onClick={() => setCollapsed((v) => !v)}
        className={`shrink-0 flex cursor-pointer items-center gap-3 border-b px-5 py-3 ${c.line}`}
      >
        {collapsed ? <ChevronRight className={`h-3 w-3 shrink-0 ${c.faint}`} /> : <ChevronDown className={`h-3 w-3 shrink-0 ${c.faint}`} />}
        <span className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>// Original submission</span>
        {canExpand && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(true) }}
            className={`ml-auto shrink-0 rounded p-1 transition-colors hover:text-signal ${c.faint}`}
            aria-label="Expand"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="flex-1 min-h-0 overflow-auto p-5">
          {content}
        </div>
      )}
    </section>
  )
}
